import React, { useState } from 'react';
import { useCurrentUser, useTranslate, useShallowEqualSelector } from '@hooks';
import classNames from 'classnames';
import { TablePaginated } from '@dotone/react-ui-core';
import ColumnForm from './AffiliateColumnForm';
import FilterForm from './AffiliateFilterForm';
import FilterList from './AffiliateFilterList';
import { CampaignTableModal } from '../Campaigns';
import {
  formatCategoryGroups,
  formatApprovalStatus,
  formatAffiliate,
  formatNetworkPerformance,
  formatNetworkHistory,
  formatInternalNotes,
} from './formatters';
import { formatDefaultPayouts, formatCustomPayouts } from '../Campaigns/formatters';
import { formatSiteInfos, formatOffer } from '../formatters';

const getColumns = ({ t, tHint, currentUser, full, onCampaignOpen }) => [
  {
    dataField: 'id',
    text: t('name'),
    hintProps: { t, tKey: 'affiliate:hints.name', html: true },
    formatter: (cell, row) => formatAffiliate(row, full, 'table'),
    headerAlign: 'center',
    classes: 'text-center',
    hidden: false,
  },
  {
    dataField: 'categoryGroups',
    text: t('table.categoryGroup'),
    hidden: full ? undefined : false,
    formatter: (cell) => (cell.length ? formatCategoryGroups(cell) : t('common:na')),
  },
  {
    dataField: 'siteInfos',
    text: t('siteInfos'),
    headerStyle: {
      width: !full && '10%',
    },
    hidden: full ? undefined : false,
    formatter: (cell, row) => (cell.length ? formatSiteInfos(cell, row) : t('common:na')),
  },
  {
    dataField: 'networkPerformance',
    text: t('networkPerformance'),
    hintProps: { t, tKey: 'affiliate:hints.networkPerformance' },
    hidden: full ? undefined : true,
    style: { width: '220px' },
    formatter: (cell, row) => formatNetworkPerformance(row, t, tHint, 'table'),
  },
  {
    dataField: 'networkHistory',
    text: t('networkHistory'),
    hintProps: { t, tKey: 'affiliate:hints.networkHistory' },
    hidden: full ? undefined : true,
    style: { width: '220px' },
    formatter: (cell, row) => formatNetworkHistory(row, t),
  },
  {
    dataField: 'campaign.offerId',
    alias: 'offerId',
    text: t('offerManagement'),
    classes: 'text-nowrap',
    hidden: currentUser.singleOffer,
    headerAttrs: { colSpan: 4 },
    style: { width: '140px' },
    formatter: (cell, row) =>
      formatOffer(
        row.campaign.offer.brandImageMediumUrl,
        row.campaign.offer,
        `campaign-offer-${row.campaign.id}`
      ),
  },
  {
    dataField: 'campaign.defaultPayouts',
    alias: 'defaultPayouts',
    text: t(currentUser.singleOffer ? 'offerManagement' : 'campaign:model.defaultPayouts'),
    hidden: false,
    headerAttrs: { hidden: !currentUser.singleOffer, colSpan: currentUser.singleOffer ? 3 : 1 },
    formatter: (cell) => formatDefaultPayouts(cell, { t }),
  },
  {
    dataField: 'campaign.customPayouts',
    alias: 'customPayouts',
    text: t('campaign:model.customPayouts'),
    hidden: false,
    headerAttrs: { hidden: true },
    formatter: (cell) => formatCustomPayouts(cell, { t }),
  },
  {
    dataField: 'campaign.approvalStatus',
    alias: 'approvalStatus',
    text: t('campaignStatus'),
    hidden: false,
    headerAttrs: { hidden: true },
    formatter: (cell, row) => formatApprovalStatus(cell, row, { t, onCampaignOpen, currentUser }),
  },
  {
    dataField: 'preference.internalNotes',
    alias: 'internalNotes',
    text: t('internalNotes'),
    headerStyle: { width: '10%' },
    hidden: full ? undefined : false,
    formatter: (cell, row) => formatInternalNotes(row, t),
  },
];

const filterColumns = ({ columns }) => {
  return columns.map((column) => ({
    ...column,
    classes: null,
    formatter: (cell, row) => {
      if (
        ['approvalStatus', 'offerId', 'defaultPayouts', 'customPayouts'].includes(column.alias) ||
        row.isMain
      ) {
        if (column.formatter) {
          return column.formatter(cell, row);
        }
        return row[column.alias || column.dataField];
      }

      return null;
    },
    attrs: (cell, row) => {
      if (
        !['approvalStatus', 'offerId', 'defaultPayouts', 'customPayouts'].includes(column.alias)
      ) {
        if (row.isMain) {
          return { rowSpan: row.campaignsCount, className: column.classes };
        }
        return { className: classNames('d-none', column.classes) };
      }
      return { className: column.classes };
    },
  }));
};

const AffiliateTable = ({ data, ...otherProps }) => {
  const { t, tHint } = useTranslate('affiliate', 'model');
  const { currentUser } = useCurrentUser();
  const [affiliateId, setAffiliateId] = useState(null);
  const { data: affiliates } = useShallowEqualSelector(({ affiliateList }) => affiliateList);
  const currentAffiliate = affiliates.find((item) => item.id === affiliateId);
  const full = currentUser.canAccessAffiliate;

  let columns = getColumns({
    t,
    tHint,
    full,
    currentUser,
    onCampaignOpen: setAffiliateId,
  });

  columns = filterColumns({ columns });

  const newData = data.flatMap(({ campaigns, ...affiliate }) =>
    campaigns.map((campaign, index) => ({
      ...affiliate,
      campaign,
      affiliateId: affiliate.id,
      affiliateOfferId: campaign.id,
      key: `${affiliate.id}-${campaign.id}`,
      campaignsCount: campaigns.length,
      isMain: index === 0,
      hasMoreCampaigns: index === campaigns.length - 1,
    }))
  );

  return (
    <>
      <TablePaginated
        remote
        condensed
        responsive
        hover
        checkVisibility
        searchable={currentUser.pro}
        classes="stylish-table table-sm text-sm"
        keyField="key"
        headerClasses="text-wrap"
        columns={columns}
        columnRenderer={full && ((columnProps) => <ColumnForm {...columnProps} />)}
        filterRenderer={(filterProps) => <FilterForm {...filterProps} />}
        filterListRenderer={(filters) => <FilterList filters={filters} />}
        data={newData}
        {...otherProps}
      />
      {currentAffiliate && (
        <CampaignTableModal
          affiliate={currentAffiliate}
          isOpen={!!affiliateId}
          onToggle={() => setAffiliateId(null)}
        />
      )}
    </>
  );
};

export default AffiliateTable;
